// Mini modal with picture having a gray background around it (two "blocks")
import React from 'react';
import { TinyColor } from '@ctrl/tinycolor';
import { css, useTheme } from '@emotion/react';
import { type InlineEditorHints } from '@bbk/ui/inline-editing';
import { MobileDevicePreview } from '@bbkAdminComponents/preview-device/mobile-device';
import {
  type HeroImageV0,
  type HeroImageV1,
  type RestFortySixtyMiniModal2Modal,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { useCardRenderEnvironment } from '@bbkFunnelComponents/card-render-environment';
import {
  Avatar,
  CloseOfferButton,
  ModalDesktopInnerSpacer,
  ModalMobileSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '../sections';

type Props = {
  modalInfo: RestFortySixtyMiniModal2Modal;
  close: () => void;
  detailedAction: React.ReactElement;
};

const RenderFortySixtyMiniModal2V2: React.FC<Props> = ({
  modalInfo: {
    id,
    requires: {
      info: { title, details },
      person,
      hero,
    },
    mobileLayout,
  },
  close,
  detailedAction,
}) => {
  const { mobileView } = useMediaQueries();
  const theme = useTheme() as EmotionTheme;
  const backgroundColor = new TinyColor(theme.modalBackground)
    .darken(4)
    .toHexString();

  const { previewCanvas } = useCardRenderEnvironment();

  const personOrImage = () => {
    if (hero) {
      return (
        <img
          data-part="image"
          alt=""
          src={(hero as HeroImageV0).img || (hero as HeroImageV1).url}
          css={css`
            height: 100%;
            width: 100%;
            object-fit: contain;
          `}
        />
      );
    }
    return <Avatar data-part="person" person={person} vertical />;
  };

  const mobileViewContent = (
    <div className="forty-sixty-mini-2 display-grid mobile group-container">
      <div
        css={css`
          background: ${backgroundColor};
          padding: 12px 1rem 0;
        `}
        className="display-grid mobile content-container align-items--center"
      >
        <OfferTitleMobile
          title={<TitleTheme data-part="title" title={title} />}
          close={close}
        />
        <div>{personOrImage()}</div>
      </div>
      <ModalMobileSpacer className="forty-sixty-mini-2 display-grid mobile inner-spacer">
        <div data-part="description">
          <CustomerHtml value={details} />
        </div>
        <div>{detailedAction}</div>
      </ModalMobileSpacer>
    </div>
  );

  if (previewCanvas?.device === 'mobile') {
    return (
      <MobileDevicePreview mobileLayout={mobileLayout}>
        {mobileViewContent}
      </MobileDevicePreview>
    );
  }

  if (mobileView) return mobileViewContent;

  return (
    <ModalDesktopInnerSpacer
      css={css`
        max-width: 800px;
        padding: 0;
      `}
      className="forty-sixty-mini-2 display-grid desktop inner-spacer"
    >
      <div
        css={css`
          background: ${backgroundColor};
          display: grid;
          place-items: center;
        `}
        className="display-grid place-items--center"
      >
        {personOrImage()}
      </div>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          padding: 1rem 1.5rem;
        `}
      >
        <CloseOfferButton onClick={close} className="align-self--flex-end" />
        <div
          css={css`
            overflow-y: auto;
            flex: 1;
          `}
          className="display-grid place-items--center"
        >
          <div>
            <TitleTheme
              data-part="title"
              title={title}
              css={{ marginBottom: '1rem' }}
            />
            <div data-part="description">
              <CustomerHtml value={details} />
            </div>
          </div>
        </div>
        <div>{detailedAction}</div>
      </div>
    </ModalDesktopInnerSpacer>
  );
};

const FortySixtyMiniModal2V2 = Object.assign(RenderFortySixtyMiniModal2V2, {
  editorHints: {
    title: {
      path: ['requires', 'info', 'title'],
      control: 'richText',
      selector: '[data-part="title"]',
      placement: 'right',
      options: {
        formatting: false,
      },
    },
    description: {
      path: ['requires', 'info', 'details'],
      control: 'richText',
      selector: '[data-part="description"]',
      placement: 'right',
    },
    person: {
      path: ['requires', 'person'],
      control: 'group',
      selector: '[data-part="person"]',
      placement: 'bottom',
      fields: [
        {
          path: ['avatar'],
          control: 'image',
          options: {
            helpLabel: 'Recommended size: 250x250px; < 100kB',
          },
        },
        {
          path: ['name'],
          control: 'text',
          label: 'Name',
        },
        {
          path: ['title'],
          control: 'text',
          label: 'Title',
        },
        {
          path: ['preposition'],
          control: 'text',
          label: 'Preposition',
        },
        {
          path: ['organization'],
          control: 'text',
          label: 'Company',
        },
      ],
    },
    image: {
      path: ['requires', 'hero', 'url'],
      control: 'image',
      selector: '[data-part="image"]',
      placement: 'top-end',
      options: {
        helpLabel: 'Recommended size: 700x700px; < 250kB',
      },
    },
    primaryAction: {
      path: ['requires', 'forwards'],
      control: 'ctaAppearance',
      selector: '[data-part="primary-action"]',
      placement: 'top-end',
      label: 'Action',
    },
    otherActions: {
      path: ['requires', 'other_actions'],
      control: 'ctaAppearance',
      selector: '[data-part="other-action"]',
      placement: 'top-end',
      label: 'Action',
      indexAttribute: 'data-action-index',
    },
  } satisfies InlineEditorHints,
});
export { FortySixtyMiniModal2V2 };
